import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-hot-toast';

import {
  useGetCompanyOwners,
  useGetInventoryApprovers,
} from '@/modules/settings/approvals/apiHooks';
import { MAX_NUM_OF_APPROVERS } from '@/modules/settings/approvals/constant';
import { IApprovals } from '@/modules/settings/approvals/types';
import { Button, SelectField, Typography } from '@/shared/components/atoms';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import SwitchButton from '@/shared/components/atoms/SwitchButton';
import { useAuth } from '@/shared/hooks/context/auth';

import Down from '~/images/shared/arrow-down.svg';
import Up from '~/images/shared/arrow-up.svg';
import Plus from '~/images/shared/Plus.svg';
import Trash from '~/images/shared/Trash.svg';

const EditableApproverRows: React.FC<{
  approvalType?: 'technical' | 'inventory';
}> = ({ approvalType }) => {
  const { t } = useTranslation('common');

  const methods = useFormContext<IApprovals.IAddApproverFormValues>();

  const { user } = useAuth();

  const { query } = useRouter();

  const isTechnicalApproval = approvalType
    ? approvalType === 'technical'
    : query?.['tab'] === 'operations';

  const {
    data: inventoryApprovers,
    isLoading: isInventoryApproversLoading,
    fetchNextPage,
  } = useGetInventoryApprovers({
    page: 1,
    //  TODO: send the selected approver in params to retrieve in first page once implemented on the backend
    limit: 100000,
    companyId: !isTechnicalApproval ? user?.companyId : undefined,
  });

  const { data: TechnicalApprovers, isLoading: isTechnicalApproversLoading } =
    useGetCompanyOwners({
      enabled: isTechnicalApproval,
    });

  const inventoryApproversList = inventoryApprovers?.pages.flatMap(
    (page) => page.items
  );

  const { fields, append, remove, swap } = useFieldArray({
    control: methods.control,
    name: 'approvers',
  });

  const handleRemove = (index: number) => {
    remove(index);
  };

  // berfore adding a new approver, we need to check if there are any errors in the form
  // and check if the number of approvers is less than the max number of approvers
  const handleAppend = () => {
    // methods.trigger('approvers');
    if (methods.formState.errors.approvers) return;
    if (methods.getValues('approvers').length >= MAX_NUM_OF_APPROVERS) {
      toast.error(
        t('you-can-not-add-more-than-approvers-count', {
          count: MAX_NUM_OF_APPROVERS,
        })
      );
      return;
    }
    append({
      approverId: null,
      canEdit: fields.length === 0 ? 1 : 0,
    });
  };

  const handleMoveUp = (index: number) => {
    if (index === 1) return;
    swap(index, index - 1);
  };
  const handleMoveDown = (index: number) => {
    if (index === fields.length - 1) return;
    swap(index, index + 1);
  };

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...methods.watch('approvers')[index],
    };
  });

  const selectedOwnerIds = useMemo(
    () => new Set(controlledFields.map((approver) => approver.approverId)),
    [controlledFields]
  );

  return (
    <>
      {controlledFields.map((field, index) => (
        <div
          key={field.id}
          className='grid grid-cols-12 items-center gap-48 px-24 py-12 odd:bg-background-neutral-100 even:bg-background-neutral-99'
        >
          {/* ******************************** hierarchy ************************************ */}
          <div className='col-span-1 px-12'>
            <Typography variant='body2' weight='bold'>
              {index + 1}
            </Typography>
          </div>
          {/* ******************************** approver info ********************************* */}

          <div className='col-span-6 '>
            {!isTechnicalApproval ? (
              <InfiniteSelectField
                fetchNextPage={fetchNextPage}
                isLoading={isInventoryApproversLoading}
                id={`approvers.${index}.approverId`}
                getLabel={(item: IApprovals.ICompanyOwnerItem) =>
                  item?.jobTitle
                    ? ` ${item?.jobTitle} - ${item?.name || ''}`
                    : item?.name || ''
                }
                getValue={(item: IApprovals.ICompanyOwnerItem) => item.id}
                required
                isMulti={false}
                list={inventoryApproversList?.filter((owner) =>
                  owner.id === field.approverId
                    ? true
                    : !selectedOwnerIds.has(owner.id)
                )}
                placeholder={t('select-approver')}
                py='0.375rem'
                width='w-full '
              />
            ) : (
              <SelectField
                isLoading={isTechnicalApproversLoading}
                id={`approvers.${index}.approverId`}
                getLabel={(item: IApprovals.ICompanyOwnerItem) =>
                  item?.jobTitle
                    ? ` ${item?.jobTitle} - ${item?.name || ''}`
                    : item?.name || ''
                }
                getValue={(item: IApprovals.ICompanyOwnerItem) => item.id}
                required
                isMulti={false}
                list={TechnicalApprovers?.companyOwners.filter((owner) =>
                  owner.id === field.approverId
                    ? true
                    : !selectedOwnerIds.has(owner.id)
                )}
                placeholder={t('select-approver')}
                py='0.375rem'
                width='w-full '
              />
            )}
          </div>
          {/* ********************************  can edit ************************************ */}
          <div className='col-span-3 '>
            <SwitchButton
              disabled={index === 0}
              id={`approvers.${index}.canEdit`}
              valueAsBinary
            />
          </div>
          {/* *********************************  actions ************************************ */}
          <div className='col-span-2 '>
            {index !== 0 && (
              <div className='flex items-center justify-end gap-12'>
                {index !== 1 && (
                  <BaseIcon
                    size='sm'
                    svgClasses='cursor-pointer text-primary-default'
                  >
                    <Up onClick={() => handleMoveUp(index)} />
                  </BaseIcon>
                )}
                {index !== fields.length - 1 && (
                  <BaseIcon size='sm' svgClasses='cursor-pointer'>
                    <Down onClick={() => handleMoveDown(index)} />
                  </BaseIcon>
                )}
                <BaseIcon size='sm' svgClasses='cursor-pointer'>
                  <Trash onClick={() => handleRemove(index)} />
                </BaseIcon>
              </div>
            )}
          </div>
        </div>
      ))}
      <div className='flex items-center justify-between'>
        <div className={clsx('inline-block')}>
          <Button onClick={handleAppend} variant='text' size='full'>
            <Typography
              variant='subtitle1'
              weight='semiBold'
              className={clsx(
                'flex items-center justify-center gap-8 p-16  text-primary-default'
              )}
            >
              <span className=' text-primary-default'>
                <Plus />
              </span>
              {t('add-approver')}
            </Typography>
          </Button>
        </div>
      </div>
    </>
  );
};
export default EditableApproverRows;
